import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SignOut, Envelope, TrendUp, MapPin, BookOpen } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BuyerMarketplaceSection from "@/components/BuyerMarketplaceSection";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import BuyerCriteriaRequestModal from "@/components/BuyerCriteriaRequestModal";
import { PencilSimple } from "@phosphor-icons/react";
import { BuyerFunnelProfiles } from "@/components/buyer/BuyerFunnelProfiles";
import { BuyerDetailsRequest } from "@/components/buyer/BuyerDetailsRequest";
import { FunnelResultsPanel } from "@/components/FunnelResultsPanel";
import { BuyerLeadStatuses } from "@/components/buyer/BuyerLeadStatuses";
import { BuyerRequestHistory } from "@/components/buyer/BuyerRequestHistory";
import { BuyerLandingPreview } from "@/components/buyer/BuyerLandingPreview";
import { BuyerDeliveryMethod } from "@/components/buyer/BuyerDeliveryMethod";
import { US_STATES } from "@/components/shared/StateCheckboxSelector";
import { ConsentProofButton } from "@/components/leads/ConsentProofButton";

// "All states (54)" when a delivery takes every state, otherwise the list.
const statesText = (d) => {
  const codes = Object.keys(d.state_fees || {}).sort();
  if (!codes.length) return "—";
  return US_STATES.every((s) => codes.includes(s.code)) ? `All states (${codes.length})` : codes.join(", ");
};

// Buyer-only read-only dashboard (iter-30).
//   - Today's delivered leads (last 24h).
//   - This week's spend + per-state breakdown.
//   - Zero writes, zero navigation into admin-only surfaces.
//   - Iter-30.2: When the buyer is linked to more than one destination,
//     a "Destination" filter pill row appears at the top. Selecting a
//     destination re-fetches the dashboard with ?destination_id=... so all
//     metrics + tables reflect just that destination.

function fmtDollars(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

export default function BuyerDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  // "" = All destinations, otherwise a destination id.
  const [selectedDest, setSelectedDest] = useState("");
  // Iter-72: criteria change request state
  const [reqOpen, setReqOpen] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [currentCriteria, setCurrentCriteria] = useState({});

  const reloadRequests = React.useCallback(async () => {
    try {
      const [reqRes, criteriaRes] = await Promise.all([
        api.get("/buyer/criteria-requests"),
        api.get("/buyer/current-criteria"),
      ]);
      setMyRequests(reqRes.data?.requests || []);
      const first = (criteriaRes.data?.clients || [])[0];
      setCurrentCriteria(first?.intake_criteria || {});
    } catch {
      /* silent — requests panel is optional */
    }
  }, []);

  useEffect(() => { reloadRequests(); }, [reloadRequests]);

  useEffect(() => {
    let alive = true;
    const params = selectedDest ? { destination_id: selectedDest } : undefined;
    api.get("/buyer/dashboard", { params })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => {
        if (alive) setErr(e.response?.data?.detail || "Failed to load dashboard");
      });
    return () => { alive = false; };
  }, [selectedDest]);

  const signOut = async () => {
    await logout();
    toast.success("Signed out");
    nav("/login", { replace: true });
  };

  const stateRows = useMemo(
    () => (data?.week?.by_state || []).slice(0, 20),
    [data],
  );
  const destinations = data?.destinations || [];
  const hasMultipleDests = destinations.length > 1;

  if (err) {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col">
        {/* Iter-71.2: keep the "End impersonation" exit on the error
            screen too — otherwise a super_admin who impersonates a
            broken buyer account is stranded with only a Sign-out. */}
        <ImpersonationBanner />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="brut-card p-8 max-w-md">
            <div className="label-mono">Access error</div>
            <div className="font-chivo font-black text-2xl uppercase tracking-tighter">
              Cannot load dashboard
            </div>
            <div className="mt-3 text-sm text-zinc-600">{err}</div>
            <button
              data-testid="buyer-signout-error"
              onClick={signOut}
              className="mt-6 border border-black px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-black hover:text-white"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center label-mono text-xs">
        Loading dashboard…
      </div>
    );
  }

  const selectedName =
    destinations.find((d) => d.id === selectedDest)?.name || "All destinations";

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="buyer-dashboard">
      {/* Iter-71.2: role-scoped dashboards live outside <Layout>, so we
          have to mount ImpersonationBanner locally — otherwise a super_admin
          who "Test as buyer" gets stuck with no way back to the admin view. */}
      <ImpersonationBanner />
      {/* Top bar */}
      <div className="bg-black text-white border-b-4 border-black">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-3 px-4 md:px-6 py-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400">
              LeadFlow — Buyer portal
            </div>
            <div className="font-chivo font-black text-xl md:text-2xl uppercase tracking-tighter leading-none mt-1 truncate">
              Welcome, {user?.name?.split(" ")[0] || "Buyer"}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href="/help?slug=buyer-portal"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="buyer-help-link"
              className="border border-white px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-white hover:text-black inline-flex items-center gap-2"
            >
              <BookOpen size={12} weight="bold" /> How do I use this?
            </a>
            <button
              data-testid="buyer-signout"
              onClick={signOut}
              className="border border-white px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-white hover:text-black inline-flex items-center gap-2"
            >
              <SignOut size={12} weight="bold" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Iter-30.2: Destination filter pills (only when >1 destination). */}
        {hasMultipleDests && (
          <div
            className="mb-6 flex flex-wrap items-center gap-2"
            data-testid="dest-filter-pills"
          >
            <div className="label-mono mr-1">Destination</div>
            <button
              data-testid="dest-pill-all"
              onClick={() => setSelectedDest("")}
              className={
                "px-3 py-1.5 text-xs uppercase tracking-widest border-2 " +
                (selectedDest === ""
                  ? "border-black bg-black text-white"
                  : "border-zinc-300 hover:border-black")
              }
            >
              All ({destinations.length})
            </button>
            {destinations.map((d) => (
              <button
                key={d.id}
                data-testid={`dest-pill-${d.id}`}
                onClick={() => setSelectedDest(d.id)}
                className={
                  "px-3 py-1.5 text-xs uppercase tracking-widest border-2 " +
                  (selectedDest === d.id
                    ? "border-black bg-black text-white"
                    : "border-zinc-300 hover:border-black")
                }
                title={`States: ${statesText(d)}`}
              >
                {d.name}
              </button>
            ))}
            <div className="label-mono ml-auto text-zinc-500" data-testid="dest-filter-active-label">
              Showing: {selectedName}
            </div>
          </div>
        )}

        {/* Metric strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="brut-card p-5" data-testid="metric-today-count">
            <div className="label-mono">Today · leads</div>
            <div className="font-chivo font-black text-4xl mt-1">
              {data.today.count}
            </div>
            <div className="text-xs text-zinc-500 mt-1">Last 24 hours UTC</div>
          </div>
          <div className="brut-card p-5" data-testid="metric-today-spend">
            <div className="label-mono">Today · spend</div>
            <div className="font-chivo font-black text-4xl mt-1">
              {fmtDollars(data.today.spend)}
            </div>
            <div className="text-xs text-zinc-500 mt-1">Priced from state fees</div>
          </div>
          <div className="brut-card p-5 !bg-black text-white" data-testid="metric-week-spend">
            <div className="label-mono text-zinc-400">This week · spend</div>
            <div className="font-chivo font-black text-4xl mt-1">
              {fmtDollars(data.week.spend)}
            </div>
            <div className="text-xs text-zinc-400 mt-1">
              {data.week.count} lead(s) since Monday 00:00 UTC
            </div>
          </div>
        </div>

        {/* Two-column: today's leads (left), state breakdown (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 brut-card overflow-hidden">
            <div className="px-4 md:px-5 py-3 border-b border-black flex items-center gap-2 bg-white">
              <Envelope size={14} weight="bold" />
              <div className="font-chivo font-black uppercase tracking-tighter">
                Today’s leads
              </div>
              <div className="label-mono ml-auto">
                {(data.today.leads || []).length} row{(data.today.leads || []).length === 1 ? "" : "s"}
                {data.today.test_count ? ` · ${data.today.test_count} test` : ""}
              </div>
            </div>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-black text-[10px] uppercase tracking-widest bg-zinc-100">
                    <th className="text-left py-2 px-4">Delivered</th>
                    <th className="text-left py-2 px-4">Consumer</th>
                    <th className="text-left py-2 px-4">State</th>
                    <th className="text-right py-2 px-4">Fee</th>
                    <th className="text-left py-2 px-4">Lead ID</th>
                    <th className="text-left py-2 px-4">Consent proof</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.today.leads || []).map((r) => (
                    <tr
                      key={r.attempt_id}
                      className="border-b border-zinc-200"
                      data-testid={`today-lead-${r.lead_id}`}
                    >
                      <td className="py-2 px-4 font-mono text-[11px]">
                        {fmtWhen(r.delivered_at)}
                      </td>
                      <td className="py-2 px-4 text-[12px]">
                        {r.consumer_name || "—"}
                        {r.is_test && <span className="ml-1 text-[9px] bg-yellow-400 text-black px-1 uppercase" data-testid={`today-lead-test-${r.lead_id}`}>Test</span>}
                      </td>
                      <td className="py-2 px-4 font-mono text-[11px]">
                        {r.state || "—"}
                      </td>
                      <td className="py-2 px-4 text-right font-mono text-[11px] font-bold">
                        {r.is_test ? "$0 test" : r.fee ? fmtDollars(r.fee) : "—"}
                      </td>
                      <td className="py-2 px-4 font-mono text-[10px] text-zinc-500">
                        {r.lead_id.slice(0, 8)}
                      </td>
                      <td className="py-2 px-4"><ConsentProofButton leadId={r.lead_id} compact /></td>
                    </tr>
                  ))}
                  {!(data.today.leads || []).length && (
                    <tr>
                      <td colSpan={6} className="py-8 text-center label-mono">
                        No leads delivered in the last 24 hours yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden p-2 flex flex-col gap-2" data-testid="today-leads-cards">
              {(data.today.leads || []).map((r) => (
                <div key={r.attempt_id} className="border border-black bg-white p-3" data-testid={`today-lead-card-${r.lead_id}`}>
                  <div className="flex items-center gap-2 mb-1 pb-1 border-b border-zinc-200">
                    <div className="font-bold text-sm truncate flex-1">{r.consumer_name || "—"}</div>
                    {r.is_test && <span className="text-[9px] bg-yellow-400 text-black px-1 uppercase" data-testid={`today-lead-test-mobile-${r.lead_id}`}>Test</span>}
                    <span className="text-[10px] font-mono uppercase tracking-widest bg-black text-white px-1.5 py-0.5">{r.state || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1 text-xs">
                    <span className="label-mono">{fmtWhen(r.delivered_at)}</span>
                    <span className="font-mono font-bold">{r.is_test ? "$0 test" : r.fee ? fmtDollars(r.fee) : "—"}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                    <span>Lead {r.lead_id.slice(0, 8)}</span>
                    <ConsentProofButton leadId={r.lead_id} compact />
                  </div>
                </div>
              ))}
              {!(data.today.leads || []).length && (
                <div className="py-8 text-center label-mono">No leads delivered in the last 24 hours yet.</div>
              )}
            </div>
          </div>

          <div className="brut-card overflow-hidden">
            <div className="px-4 md:px-5 py-3 border-b border-black flex items-center gap-2 bg-white">
              <TrendUp size={14} weight="bold" />
              <div className="font-chivo font-black uppercase tracking-tighter">
                This week by state
              </div>
            </div>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-black text-[10px] uppercase tracking-widest bg-zinc-100">
                    <th className="text-left py-2 px-4">State</th>
                    <th className="text-right py-2 px-4">Leads</th>
                    <th className="text-right py-2 px-4">Spend</th>
                  </tr>
                </thead>
                <tbody>
                  {stateRows.map((r) => (
                    <tr
                      key={r.state}
                      className="border-b border-zinc-200"
                      data-testid={`week-state-${r.state}`}
                    >
                      <td className="py-2 px-4 font-mono font-bold">{r.state}</td>
                      <td className="py-2 px-4 text-right font-mono">{r.count}</td>
                      <td className="py-2 px-4 text-right font-mono font-bold">
                        {fmtDollars(r.spend)}
                      </td>
                    </tr>
                  ))}
                  {!stateRows.length && (
                    <tr>
                      <td colSpan={3} className="py-8 text-center label-mono">
                        No spend yet this week.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden p-2 flex flex-col gap-2" data-testid="week-state-cards">
              {stateRows.map((r) => (
                <div key={r.state} className="border border-black bg-white p-3 flex items-center justify-between gap-2" data-testid={`week-state-card-${r.state}`}>
                  <div className="font-mono font-bold text-sm">{r.state}</div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="font-mono">{r.count} leads</span>
                    <span className="font-mono font-bold">{fmtDollars(r.spend)}</span>
                  </div>
                </div>
              ))}
              {!stateRows.length && (
                <div className="py-8 text-center label-mono">No spend yet this week.</div>
              )}
            </div>
          </div>
        </div>

        <BuyerLeadStatuses />
        <BuyerDetailsRequest />
        <BuyerFunnelProfiles />
        <FunnelResultsPanel
          endpoint="/buyer/funnel-results"
          testIdPrefix="buyer-funnel-results"
          title="Your funnel results"
          subtitle="Visits, leads and deliveries from the shared funnels"
          showUnmatched={false}
          buyerView
        />

        {/* Iter-33: Buyer preview of the actual landing pages leads see */}
        <BuyerLandingPreview brandName={data?.brand_name || ""} />

        {/* Iter-35: Buyer-side marketplace — leads available for purchase */}
        <BuyerMarketplaceSection destinations={data?.destinations || []} />

        {/* Iter-72: Buyer criteria change requests */}
        <div className="mt-8 brut-card p-5" data-testid="buyer-criteria-requests">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <PencilSimple size={14} weight="bold" />
            <div className="font-chivo font-black uppercase tracking-tighter">
              Intake criteria
            </div>
            <div className="label-mono text-zinc-500">
              What kind of leads you accept
            </div>
            <button
              onClick={() => setReqOpen(true)}
              data-testid="buyer-open-criteria-request"
              className="ml-auto border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1"
            >
              <PencilSimple size={12} weight="bold" /> Request change
            </button>
          </div>
          <p className="text-[11px] text-zinc-600 leading-relaxed">
            You can propose changes to your intake criteria at any time —
            your admin will review and either approve or reject with feedback.
            Nothing goes live until an admin approves.
          </p>
          <BuyerRequestHistory requests={myRequests} />
        </div>

        {/* Destinations connected */}
        <div className="mt-8 brut-card p-5" data-testid="buyer-destinations-summary">
          <div className="flex items-center gap-2 mb-3">
            <MapPin size={14} weight="bold" />
            <div className="font-chivo font-black uppercase tracking-tighter">
              Your delivery destinations
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {destinations.map((d) => (
              <div
                key={d.id}
                className={
                  "border p-3 " +
                  (selectedDest === d.id ? "border-black bg-zinc-100" : "border-black")
                }
              >
                <div className="font-bold">{d.name}</div>
                <div className="label-mono mt-1">
                  {d.payment_mode}
                  {d.invoice_enabled ? " · weekly invoice ON" : ""}
                </div>
                <BuyerDeliveryMethod dest={d} />
                <div className="text-[11px] text-zinc-600 mt-1" data-testid={`buyer-dest-states-${d.id}`}>
                  States: {statesText(d)}
                </div>
                <button type="button" onClick={() => setReqOpen(true)} data-testid={`buyer-dest-change-states-${d.id}`}
                        className="mt-2 inline-flex items-center gap-1 border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                  <PencilSimple size={11} weight="bold" /> Change states
                </button>
              </div>
            ))}
            {!destinations.length && (
              <div className="label-mono text-zinc-500" data-testid="buyer-destinations-empty">
                No delivery is linked to this login yet, so we can&apos;t show how your leads arrive. Your account manager can link it.
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 text-xs text-zinc-500 leading-relaxed">
          Read-only view. Reply to your daily digest email if you need any lead
          re-sent or reported as bad — our team monitors that address.
        </div>
      </div>

      {/* Iter-72: mounted at the root so it's positioned relative to the
          viewport, not the constrained max-w-6xl column. */}
      <BuyerCriteriaRequestModal
        open={reqOpen}
        onClose={() => setReqOpen(false)}
        currentCriteria={currentCriteria}
        onSubmitted={reloadRequests}
      />
    </div>
  );
}
